// =============================================================================
// screens/StoryCreate.tsx — "Create Story" (P3-10 MEDIA/STORIES).
//
// A real Instagram-grade CAPTURE / COMPOSE flow (not a form dialog):
//   • Full-screen DARK compose canvas — the story fills the screen edge-to-edge,
//     so you author on the exact 9:16 surface it will be viewed on. No lavender
//     "form" chrome, no empty voids.
//   • Bottom MODE STRIP (Photo · Gallery · Text) — swipeable-feeling segmented
//     glass control; picking a source drops you straight into the composed canvas
//     with the media as the backdrop.
//   • For TEXT stories: a live gradient card you type onto (real TEXT story type).
//   • For MEDIA stories: a tappable "Add text" overlay you can position + restyle
//     (subtle / bold / boxed) with a glass style rail.
//   • Glass top bar (close + reset) and a glass PUBLISH bar, both mode-'dark'
//     GlassSurfaces so they read as real frosted controls over the media.
//   • Responsive: the compose canvas is centred + constrained to a 9:16 phone
//     frame on tablet/desktop (never a stretched full-window image).
//
// NATIVE NOTE: capture/selection uses react-native-image-picker (already a dep).
//   launchCamera is a *system camera* capture — there is no in-app live camera
//   preview because no native camera lib (e.g. react-native-vision-camera) is
//   installed. See followUps. Video capture would also need react-native-video
//   for a live preview; images are the shipped media path here.
//
// Backend contract UNCHANGED: uploadMedia (services/api) then POST /stories with
//   { type:'IMAGE'|'TEXT', mediaUrl?, text? } (see apps/api createStorySchema).
// =============================================================================
import { useState, useRef, useCallback, useLayoutEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  Alert,
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { launchCamera, launchImageLibrary } from 'react-native-image-picker';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons, type IoniconName } from '../components/ui/Icon';
import { GlassSurface, ActionSheet } from '../components/glass';
import type { ActionSheetOption } from '../components/glass';
import { glassTokens } from '../components/glass';
import type { RootStackParamList } from '../navigation/types';
import {
  colors,
  spacing,
  radius,
  fonts,
  typographyScale,
  shadowTokens,
  colorTokens,
  getLayout,
} from '../constants/theme';
import { api, uploadMedia, getLastUploadError } from '../services/api';

type Nav = NativeStackNavigationProp<RootStackParamList, 'StoryCreate'>;

// Compose modes. 'media' is entered once a photo is chosen (camera/gallery);
// 'text' is a pure gradient text story. 'pick' is the initial empty state.
type Mode = 'pick' | 'media' | 'text';

// Text overlay style presets (media stories) — subtle / bold / boxed.
type TextStyleKey = 'subtle' | 'bold' | 'boxed';

const dark = colorTokens.dark;

const TEXT_GRADIENTS: [string, string][] = [
  [colors.primary, colors.primaryLight],
  ['#0EA5E9', colors.primary],
  ['#F59E0B', '#FF4757'],
  ['#2DB55D', '#0EA5E9'],
];

export default function StoryCreateScreen() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const layout = getLayout(width);

  // Kill the opaque white "Create Story" native header — this is a full-screen
  // dark compose modal that paints its OWN translucent glass chrome (topChrome /
  // bottomChrome below), so content flows edge-to-edge under it (Apple design §12).
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  const [mode, setMode] = useState<Mode>('pick');
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [textOverlay, setTextOverlay] = useState('');
  const [textStyle, setTextStyle] = useState<TextStyleKey>('boxed');
  const [gradientIdx, setGradientIdx] = useState(0);
  const [editingOverlay, setEditingOverlay] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Share destination sheet (IG-style): the compose CTA opens a dark selectable
  // sheet, and the sheet's own pinned gradient CTA runs the real publish. The
  // destinations are a visual audience selector — the /stories contract is
  // audience-agnostic, so every selection publishes via the unchanged
  // handlePublish() below (no backend field is invented).
  const [shareOpen, setShareOpen] = useState(false);
  const [destinations, setDestinations] = useState<string[]>(['story']);
  const overlayInputRef = useRef<TextInput>(null);

  const shareDestinations: ActionSheetOption[] = [
    { key: 'story', label: 'Your Story', sublabel: 'Everyone who follows you', icon: 'radio-outline' },
    { key: 'closeFriends', label: 'Close Friends', sublabel: 'Only people on your list', icon: 'star-outline' },
  ];

  const toggleDestination = useCallback((key: string) => {
    setDestinations((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  // ── Compose frame geometry ────────────────────────────────────────────────
  // On phone the canvas is full-bleed. On tablet/desktop we constrain to a real
  // 9:16 story frame, centred, so media is never stretched across a wide window.
  const frameWidth =
    layout === 'phone'
      ? width
      : Math.min(width - spacing.xxxl * 2, ((height - spacing.xxxl * 2) * 9) / 16, 460);
  const frameHeight = layout === 'phone' ? height : (frameWidth * 16) / 9;
  const isFramed = layout !== 'phone';

  // ── Source pickers (system camera / gallery) ──────────────────────────────
  const handlePickCamera = useCallback(async () => {
    try {
      const result = await launchCamera({ mediaType: 'photo', quality: 0.9, cameraType: 'back' });
      if (result.didCancel) return;
      if (result.errorCode) {
        Alert.alert('Error', result.errorMessage || 'Could not open camera');
        return;
      }
      const uri = result.assets?.[0]?.uri;
      if (uri) {
        setImageUri(uri);
        setMode('media');
      }
    } catch {
      Alert.alert('Error', 'Could not open camera');
    }
  }, []);

  const handlePickGallery = useCallback(async () => {
    try {
      const result = await launchImageLibrary({ mediaType: 'photo', quality: 0.9, selectionLimit: 1 });
      if (result.didCancel) return;
      if (result.errorCode) {
        Alert.alert('Error', result.errorMessage || 'Could not open gallery');
        return;
      }
      const uri = result.assets?.[0]?.uri;
      if (uri) {
        setImageUri(uri);
        setMode('media');
      }
    } catch {
      Alert.alert('Error', 'Could not open gallery');
    }
  }, []);

  const handleReset = useCallback(() => {
    setImageUri(null);
    setTextOverlay('');
    setEditingOverlay(false);
    setMode('pick');
  }, []);

  const canPublish = (mode === 'media' && !!imageUri) || (mode === 'text' && textOverlay.trim().length > 0);

  async function handlePublish() {
    if (!canPublish || publishing) return;
    setPublishing(true);
    try {
      let body: Record<string, any>;
      if (mode === 'text') {
        // Real TEXT story — no upload needed.
        body = { type: 'TEXT', text: textOverlay.trim() };
      } else {
        const uploaded = await uploadMedia(imageUri!);
        if (!uploaded) {
          setPublishing(false);
          Alert.alert('Upload Failed', getLastUploadError() || 'Could not upload the image. Please try again.');
          return;
        }
        body = { type: 'IMAGE', mediaUrl: uploaded };
        if (textOverlay.trim()) body.text = textOverlay.trim();
      }

      const res = await api('/stories', { method: 'POST', body: JSON.stringify(body) });
      setPublishing(false);
      if (res.success) {
        setShareOpen(false);
        navigation.goBack();
      } else {
        Alert.alert('Error', res.error || 'Failed to publish story');
      }
    } catch (err: any) {
      setPublishing(false);
      Alert.alert('Error', err?.message || 'Something went wrong. Please try again.');
    }
  }

  const topPad = insets.top + (isFramed ? spacing.xl : spacing.sm);
  const bottomPad = Math.max(insets.bottom, spacing.lg);

  // ── The compose canvas (backdrop + overlays) ──────────────────────────────
  const canvas = (
    <View
      style={[
        styles.canvas,
        isFramed
          ? { width: frameWidth, height: frameHeight, borderRadius: radius.xl, ...shadowTokens.sheet }
          : styles.fill,
      ]}
    >
      {/* Backdrop layer */}
      {mode === 'text' ? (
        <LinearGradient
          colors={TEXT_GRADIENTS[gradientIdx]}
          start={{ x: 0.1, y: 0 }}
          end={{ x: 0.9, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
      ) : imageUri ? (
        <Image source={{ uri: imageUri }} style={StyleSheet.absoluteFill} resizeMode="cover" />
      ) : (
        <LinearGradient
          colors={[dark.surface, dark.bg]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0.6, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
      )}

      {/* Legibility scrims (top for chrome, bottom for actions) — only over media */}
      {mode === 'media' && (
        <>
          <LinearGradient
            colors={['rgba(14,11,26,0.55)', 'rgba(14,11,26,0)']}
            style={styles.scrimTop}
            pointerEvents="none"
          />
          <LinearGradient
            colors={['rgba(14,11,26,0)', 'rgba(14,11,26,0.65)']}
            style={styles.scrimBottom}
            pointerEvents="none"
          />
        </>
      )}

      {/* Empty pick state — invitation, never a void */}
      {mode === 'pick' && (
        <View style={styles.pickState} pointerEvents="none">
          <View style={styles.pickIconRing}>
            <Ionicons name="sparkles" size={30} color={colors.primaryLight} />
          </View>
          <Text style={styles.pickTitle}>Create your story</Text>
          <Text style={styles.pickSub}>Snap a photo, pick from your gallery,{'\n'}or write a text story</Text>
        </View>
      )}

      {/* TEXT story editor */}
      {mode === 'text' && (
        <Pressable style={styles.textEditWrap} onPress={() => overlayInputRef.current?.focus()}>
          <TextInput
            ref={overlayInputRef}
            style={styles.textStoryInput}
            placeholder="Type something…"
            placeholderTextColor="rgba(255,255,255,0.6)"
            value={textOverlay}
            onChangeText={setTextOverlay}
            multiline
            maxLength={240}
            autoFocus
            selectionColor={colors.white}
            textAlign="center"
          />
        </Pressable>
      )}

      {/* MEDIA text overlay (tap to add/edit) */}
      {mode === 'media' && (
        <Pressable
          style={styles.overlayZone}
          onPress={() => {
            setEditingOverlay(true);
            setTimeout(() => overlayInputRef.current?.focus(), 50);
          }}
        >
          {editingOverlay ? (
            <TextInput
              ref={overlayInputRef}
              style={[styles.overlayInput, textStyleFor(textStyle)]}
              placeholder="Add text…"
              placeholderTextColor="rgba(255,255,255,0.7)"
              value={textOverlay}
              onChangeText={setTextOverlay}
              multiline
              maxLength={120}
              selectionColor={colors.white}
              textAlign="center"
              onBlur={() => setEditingOverlay(false)}
              blurOnSubmit
            />
          ) : textOverlay.trim().length > 0 ? (
            <Text style={[styles.overlayText, textStyleFor(textStyle)]} numberOfLines={5}>
              {textOverlay}
            </Text>
          ) : (
            <View style={styles.overlayHint} pointerEvents="none">
              <Ionicons name="text" size={16} color="rgba(255,255,255,0.85)" />
              <Text style={styles.overlayHintText}>Tap to add text</Text>
            </View>
          )}
        </Pressable>
      )}
    </View>
  );

  return (
    <KeyboardAvoidingView
      style={styles.root}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {/* Backdrop behind the framed canvas on tablet/desktop */}
      <View style={styles.stage}>{canvas}</View>

      {/* ── Top glass chrome ── */}
      <View style={[styles.topChrome, { top: topPad }]} pointerEvents="box-none">
        <GlassSurface
          mode="dark"
          variant="pill"
          radius={glassTokens.radius.pill}
          onPress={() => navigation.goBack()}
          style={styles.chromeBtn}
          contentStyle={styles.chromeBtnInner}
          accessibilityLabel="Close story creator"
        >
          <Ionicons name="close" size={22} color={colors.white} />
        </GlassSurface>

        <View style={styles.brandPill}>
          <Ionicons name="sparkles" size={13} color={colors.primaryLight} />
          <Text style={styles.brandPillText}>New Story</Text>
        </View>

        {mode !== 'pick' ? (
          <GlassSurface
            mode="dark"
            variant="pill"
            radius={glassTokens.radius.pill}
            onPress={handleReset}
            style={styles.chromeBtn}
            contentStyle={styles.chromeBtnInner}
            accessibilityLabel="Discard and start over"
          >
            <Ionicons name="refresh" size={20} color={colors.white} />
          </GlassSurface>
        ) : (
          <View style={styles.chromeBtn} />
        )}
      </View>

      {/* ── Right style rail (media text styling / text gradient swatches) ── */}
      {mode === 'media' && (textOverlay.trim().length > 0 || editingOverlay) && (
        <View style={[styles.styleRail, { top: topPad + spacing.xxxl + spacing.sm }]} pointerEvents="box-none">
          {(['subtle', 'bold', 'boxed'] as TextStyleKey[]).map((k) => (
            <StyleChip key={k} k={k} active={textStyle === k} onPress={() => setTextStyle(k)} />
          ))}
        </View>
      )}
      {mode === 'text' && (
        <View style={[styles.styleRail, { top: topPad + spacing.xxxl + spacing.sm }]} pointerEvents="box-none">
          {TEXT_GRADIENTS.map((g, i) => (
            <Pressable
              key={i}
              onPress={() => setGradientIdx(i)}
              accessibilityRole="button"
              accessibilityLabel={`Background ${i + 1}`}
              accessibilityState={{ selected: gradientIdx === i }}
              style={({ pressed }) => [
                styles.swatchWrap,
                gradientIdx === i && styles.swatchWrapActive,
                pressed && styles.swatchWrapPressed,
              ]}
            >
              <LinearGradient colors={g} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.swatch} />
            </Pressable>
          ))}
        </View>
      )}

      {/* ── Bottom controls: mode strip + publish ── */}
      <View style={[styles.bottomChrome, { paddingBottom: bottomPad }]} pointerEvents="box-none">
        {mode === 'pick' ? (
          <View style={styles.modeStrip}>
            <ModeAction icon="camera" label="Camera" onPress={handlePickCamera} accent />
            <ModeAction icon="images" label="Gallery" onPress={handlePickGallery} />
            <ModeAction icon="text" label="Text" onPress={() => { setMode('text'); setTextOverlay(''); }} />
          </View>
        ) : (
          <GlassSurface
            mode="dark"
            variant="pill"
            radius={glassTokens.radius.pill}
            onPress={() => setShareOpen(true)}
            disabled={!canPublish || publishing}
            style={styles.publishWrap}
            contentStyle={styles.publishInner}
            surfaceColor={colors.primary}
            accessibilityLabel="Choose where to share"
          >
            {publishing ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <>
                <Text style={styles.publishText}>Share</Text>
                <View style={styles.publishArrow}>
                  <Ionicons name="arrow-forward" size={18} color={colors.primary} />
                </View>
              </>
            )}
          </GlassSurface>
        )}
      </View>

      {/* ── Share destination sheet (IG-style dark selectable rows + pinned
          gradient CTA). Rebuilt on the shared ActionSheet foundation so the
          share step reads as a real frosted sheet, not a bare button. The
          pinned CTA runs the UNCHANGED handlePublish() → POST /stories. ── */}
      <ActionSheet
        visible={shareOpen}
        onClose={() => { if (!publishing) setShareOpen(false); }}
        title="Share to"
        options={shareDestinations}
        selected={destinations}
        onSelect={toggleDestination}
        ctaTitle="Share story"
        onCta={handlePublish}
        ctaLoading={publishing}
        ctaDisabled={!canPublish || destinations.length === 0}
      />
    </KeyboardAvoidingView>
  );
}

// ── Text-style presets used by the media overlay ──────────────────────────────
function textStyleFor(k: TextStyleKey) {
  switch (k) {
    case 'subtle':
      return stylePresets.subtle;
    case 'bold':
      return stylePresets.bold;
    case 'boxed':
    default:
      return stylePresets.boxed;
  }
}

// ── Small compose sub-components ──────────────────────────────────────────────
function ModeAction({
  icon,
  label,
  onPress,
  accent,
}: {
  icon: IoniconName;
  label: string;
  onPress: () => void;
  accent?: boolean;
}) {
  return (
    <Pressable
      style={({ pressed }) => [styles.modeAction, pressed && styles.modeActionPressed]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      {accent ? (
        <LinearGradient
          colors={[colors.primary, colors.primaryLight]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.modeIconAccent}
        >
          <Ionicons name={icon} size={26} color={colors.white} />
        </LinearGradient>
      ) : (
        <GlassSurface mode="dark" variant="card" radius={glassTokens.radius.md} contentStyle={styles.modeIcon}>
          <Ionicons name={icon} size={24} color={colors.white} />
        </GlassSurface>
      )}
      <Text style={styles.modeLabel}>{label}</Text>
    </Pressable>
  );
}

function StyleChip({ k, active, onPress }: { k: TextStyleKey; active: boolean; onPress: () => void }) {
  return (
    <GlassSurface
      mode="dark"
      variant="pill"
      radius={glassTokens.radius.pill}
      onPress={onPress}
      surfaceColor={active ? colors.primary : undefined}
      style={styles.styleChip}
      contentStyle={styles.styleChipInner}
      accessibilityLabel={`${k} text style`}
    >
      <Text style={[styles.styleChipGlyph, active && styles.styleChipGlyphActive]}>Aa</Text>
    </GlassSurface>
  );
}

const stylePresets = StyleSheet.create({
  subtle: {
    color: colors.white,
    ...typographyScale.h3,
    ...fonts.semibold,
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 6,
  },
  bold: {
    color: colors.white,
    fontSize: 30,
    lineHeight: 36,
    ...fonts.extrabold,
    letterSpacing: -0.4,
    textShadowColor: 'rgba(0,0,0,0.55)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 10,
  },
  boxed: {
    color: colors.white,
    ...typographyScale.h3,
    ...fonts.bold,
    backgroundColor: 'rgba(26,26,46,0.62)',
    borderRadius: radius.md,
    overflow: 'hidden',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
});

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: dark.bg,
  },
  fill: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  stage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  canvas: {
    overflow: 'hidden',
    backgroundColor: dark.surface,
  },
  scrimTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 160,
  },
  scrimBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 220,
  },

  // Empty pick state
  pickState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    padding: spacing.xxl,
  },
  pickIconRing: {
    width: 76,
    height: 76,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(168,85,247,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(168,85,247,0.4)',
    marginBottom: spacing.md,
    shadowColor: colors.primary,
    shadowOpacity: 0.45,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    elevation: 0,
  },
  pickTitle: {
    ...typographyScale.h1,
    letterSpacing: -0.6,
    color: colors.white,
    textAlign: 'center',
  },
  pickSub: {
    ...typographyScale.body,
    lineHeight: 21,
    color: dark.textMuted,
    textAlign: 'center',
    maxWidth: 280,
  },

  // TEXT story
  textEditWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xxl,
  },
  textStoryInput: {
    width: '100%',
    color: colors.white,
    fontSize: 28,
    lineHeight: 38,
    textAlign: 'center',
    ...fonts.bold,
    textShadowColor: 'rgba(0,0,0,0.35)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 8,
  },

  // MEDIA overlay
  overlayZone: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.xl,
  },
  overlayInput: {
    minWidth: '60%',
    textAlign: 'center',
  },
  overlayText: {
    textAlign: 'center',
  },
  overlayHint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: 'rgba(26,26,46,0.45)',
    borderRadius: radius.full,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  overlayHintText: {
    ...typographyScale.caption,
    color: 'rgba(255,255,255,0.85)',
    ...fonts.semibold,
  },

  // Top chrome
  topChrome: {
    position: 'absolute',
    left: spacing.lg,
    right: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 10,
  },
  chromeBtn: {
    width: 40,
    height: 40,
  },
  chromeBtnInner: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  brandPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: 'rgba(26,26,46,0.5)',
    borderRadius: radius.full,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 2,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  brandPillText: {
    ...typographyScale.caption,
    color: colors.white,
    ...fonts.semibold,
  },

  // Style rail (right side)
  styleRail: {
    position: 'absolute',
    right: spacing.lg,
    alignItems: 'center',
    gap: spacing.sm,
    zIndex: 10,
  },
  styleChip: {
    width: 44,
    height: 44,
  },
  styleChipInner: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  styleChipGlyph: {
    fontSize: 17,
    color: colors.white,
    ...fonts.bold,
  },
  styleChipGlyphActive: {
    color: colors.white,
  },
  swatchWrap: {
    width: 36,
    height: 36,
    borderRadius: radius.full,
    padding: 2,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  swatchWrapActive: {
    borderColor: colors.white,
  },
  swatchWrapPressed: {
    transform: [{ scale: 0.9 }],
  },
  swatch: {
    flex: 1,
    borderRadius: radius.full,
  },

  // Bottom chrome
  bottomChrome: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: spacing.xl,
    zIndex: 10,
  },
  modeStrip: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: spacing.xxxl,
  },
  modeAction: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  modeActionPressed: {
    opacity: 0.82,
    transform: [{ scale: 0.96 }],
  },
  modeIcon: {
    width: 60,
    height: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modeIconAccent: {
    width: 68,
    height: 68,
    borderRadius: radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadowTokens.card,
    shadowColor: colors.primary,
  },
  modeLabel: {
    ...typographyScale.caption,
    letterSpacing: 0.4,
    color: 'rgba(255,255,255,0.92)',
    ...fonts.semibold,
  },

  // Publish bar
  publishWrap: {
    alignSelf: 'stretch',
  },
  publishInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 56,
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  publishText: {
    ...typographyScale.body,
    color: colors.white,
    ...fonts.bold,
    fontSize: 16,
  },
  publishArrow: {
    width: 32,
    height: 32,
    borderRadius: radius.full,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
